import { api } from '../../api-helper';
import { URLS } from '../../../constants/URLS';

export const DEFAULT_NOTIFICATION_PAGE_SIZE = 10;

const onlyAssigned = (scope) => scope === 'mine';

// Maps a raw notification (from either the REST list or a WebSocket push —
// both share the same shape) into the row the UI renders.
export const mapNotificationRow = (raw) => ({
  id: raw.id,
  message: raw.message,
  createdAt: raw.createdAt,
  documentID: raw.documentID,
  target: {
    type: 'order',
    label: raw.title,
    value: raw.orderDetails?.id,
    documentID: raw.documentID
  }
});

const toSection = (section) => ({
  key: section.label,
  total: section.unreadCount || 0,
  items: (section.results || []).map(mapNotificationRow),
  nextCursor: section.nextID || null,
  hasMore: Boolean(section.nextID)
});

export const getNotificationSummary = ({ scope = 'all' } = {}) =>
  api
    .get(URLS.clientNotificationUnreadCount, {
      params: { byCategory: true, only_assigned_orders: onlyAssigned(scope) },
      headers: { needsToken: true }
    })
    .then(({ data }) => ({
      data: {
        total: data.unreadCount || 0,
        categories: (data.results || []).map((r) => ({
          key: r.label,
          total: r.unreadCount || 0
        }))
      }
    }));

export const getNotificationSections = ({ scope = 'all' } = {}) =>
  api
    .get(URLS.clientNotifications, {
      params: {
        limit: DEFAULT_NOTIFICATION_PAGE_SIZE,
        only_assigned_orders: onlyAssigned(scope)
      },
      headers: { needsToken: true }
    })
    .then(({ data }) => ({
      data: {
        total: data.unreadCount || 0,
        sections: (data.sections || [])
          .filter((s) => (s.results || []).length > 0)
          .map(toSection)
      }
    }));

export const getCategoryNotifications = ({
  category,
  scope = 'all',
  cursor = null,
  limit = DEFAULT_NOTIFICATION_PAGE_SIZE
} = {}) =>
  api
    .get(URLS.clientNotifications, {
      params: {
        limit,
        only_assigned_orders: onlyAssigned(scope),
        category,
        ...(cursor && { page: cursor })
      },
      headers: { needsToken: true }
    })
    .then(({ data }) => {
      const sections = data.sections || [];
      const section =
        sections.find((s) => s.label === category) || sections[0] || {};
      return {
        data: {
          total: section.unreadCount || 0,
          items: (section.results || []).map(mapNotificationRow),
          nextCursor: section.nextID || null,
          hasMore: Boolean(section.nextID)
        }
      };
    });

export const markNotificationRead = (notificationId) =>
  api.put(URLS.markClientNotificationRead, null, {
    params: { id: notificationId },
    headers: { needsToken: true }
  });

export const markCategoryNotificationsRead = (category, scope = 'all') =>
  api.put(URLS.markAllClientNotificationsRead, null, {
    params: { category, only_assigned_orders: onlyAssigned(scope) },
    headers: { needsToken: true }
  });

// No category = clear every category in scope.
export const markAllNotificationsRead = (scope = 'all') =>
  api.put(URLS.markAllClientNotificationsRead, null, {
    params: { only_assigned_orders: onlyAssigned(scope) },
    headers: { needsToken: true }
  });
